import { useEffect, useState } from "react";

import { CheckIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import { IPendingPartCategory } from "../../models/PendingCategory/PendingCategory";
import { Table } from "../../components/base/Table/Table";
import { PendingCategoryQueryKeys } from "../../data/PendingCategory/keys";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import usePendingCategoryMutations from "../../data/PendingCategory/usePendingCategoryMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";

export default function PendingCategories() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { confirm, remove } = usePendingCategoryMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Pending category");
  const { addNotification } = useNotification();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCategory, setConfirmCategory] = useState<IPendingPartCategory | undefined>(undefined);

  const colHelper = createColumnHelper<IPendingPartCategory>();

  useEffect(() => {
    setInfo({
      name: "Pending Categories",
      desc: "Review, edit and confirm categories waiting to be added",
      pageTitle: "Pending Categories",
    });
  }, []);


  const cols = [
    colHelper.accessor("id", { header: "Category ID" }),
    colHelper.accessor("name", { header: "Category Name" }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <CheckIcon
            className="w-5 h-5 text-green-600 opacity-70 hover:opacity-100 hover:cursor-pointer"
            title="Confirm & add"
            onClick={() => { setConfirmCategory(info.row.original); setConfirmOpen(true); }}
          />
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            title="Review & edit"
            onClick={() => navigate(`/pending-categories/${info.row.original.id}`)}
          />
          <TrashIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            title="Reject"
            onClick={() => { setDeleteId(info.row.original.id); setDeleteOpen(true); }}
          />
        </div>
      ),
    }),
  ];


  const handleRejectCategory = () => {
    if (!deleteId) return;
    remove.mutate(deleteId, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
      },
    });
  };

  const handleConfirmCategory = () => {
    if (!confirmCategory) return;
    confirm.mutate(confirmCategory, {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: "Category confirmed & added" });
        setConfirmOpen(false);
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: "Failed to confirm category" });
      },
    });
  };

  return (
    <div className="flex flex-col w-full h-full gap-4">

      <ConfirmModal
        open={deleteOpen} setOpen={setDeleteOpen} title={"Confirm Rejection"}
        message={`Are you sure you want to reject this pending category?\n It will be discarded and not added to your categories.`}
        confirmButtonText={"Reject"}
        onConfirm={handleRejectCategory}
        isLoading={remove.isPending}
      />

      <ConfirmModal
        open={confirmOpen} setOpen={setConfirmOpen} title={"Confirm & Add"}
        message={`Add "${confirmCategory?.name ?? ""}" to your part categories?\n This will move it out of the pending list.`}
        confirmButtonText={"Confirm & Add"}
        onConfirm={handleConfirmCategory}
        isLoading={confirm.isPending}
      />

      <Table
        url="/part/pending-categories"
        queryKey={PendingCategoryQueryKeys.all()}
        columns={cols}
        searchLabel="Search by name or ID"
      />
    </div>
  );
}
